import React, { useRef, useState } from "react";
import { Editor } from "@tinymce/tinymce-react";
import "tinymce/tinymce";
import "tinymce/icons/default";
import "tinymce/themes/silver";
import "tinymce/models/dom";
import "tinymce/plugins/advlist";
import "tinymce/plugins/autolink";
import "tinymce/plugins/charmap";
import "tinymce/plugins/code";
import "tinymce/plugins/codesample";
import "tinymce/plugins/directionality";
import "tinymce/plugins/emoticons";
import "tinymce/plugins/emoticons/js/emojis";
import "tinymce/plugins/fullscreen";
import "tinymce/plugins/image";
import "tinymce/plugins/insertdatetime";
import "tinymce/plugins/link";
import "tinymce/plugins/lists";
import "tinymce/plugins/media";
import "tinymce/plugins/nonbreaking";
import "tinymce/plugins/pagebreak";
import "tinymce/plugins/preview";
import "tinymce/plugins/quickbars";
import "tinymce/plugins/searchreplace";
import "tinymce/plugins/table";
import "tinymce/plugins/visualblocks";
import "tinymce/plugins/visualchars";
import "tinymce/plugins/wordcount";
import "tinymce/skins/ui/oxide/skin.min.css";
import "tinymce/skins/content/default/content.min.css";
import axios from "axios";
import config from "../../services/config";
import { getTokenSession } from "../../utils/common";
import MediaLibraryModal from "../MediaLibraryModal";
import swal from "sweetalert";

const toImageSlug = (value = "image") =>
  value
    .replace(/\.[^/.]+$/, "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-|-$/g, "") || "image";

const CKEditors = ({ data, update, label, folder_name, page_type }) => {
  const [loading, setLoading] = useState(false);
  const [mediaOpen, setMediaOpen] = useState(false);
  const editorRef = useRef(null);
  const apiurl = `${config.apiEndPoint}innerPages/uploadImage`;
  const authentication = getTokenSession();

  const uploadImage = async (blobInfo) => {
    setLoading(true);

    if (!blobInfo.filename().toLowerCase().endsWith(".webp")) {
      swal("Only .webp images can be uploaded", "Please choose a WEBP image.", "warning");
      setLoading(false);
      throw new Error("Only WEBP images are allowed.");
    }

    const formData = new FormData();
    const imageSlug = toImageSlug(blobInfo.filename());
    formData.append("image", blobInfo.blob(), blobInfo.filename());
    formData.append("folder_name", (folder_name || "").replaceAll("_", "-"));
    formData.append("page_type", page_type || "");
    formData.append("type", "image");
    formData.append("alt", imageSlug);
    formData.append("slug", imageSlug);

    try {
      const response = await axios.post(apiurl, formData, {
        headers: {
          "Content-Type": "multipart/form-data",
          Authorization: `Bearer ${authentication}`,
        },
      });

      const imagePath = response.data?.data?.image_full_path;

      if (!imagePath) {
        throw new Error("Image upload response did not include image_full_path.");
      }

      return imagePath;
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="relative">
      {loading && (
        <div role="status" className="absolute right-0 top-[-2rem]">
          <svg className="w-6 h-6 ml-auto text-gray-200 animate-spin dark:text-gray-600 fill-blue-600" viewBox="0 0 100 101" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z" fill="currentColor" />
            <path d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z" fill="currentFill" />
          </svg>
        </div>
      )}
      <div className="mt-2 bg-white">
        <Editor
          value={data || ""}
          onEditorChange={(content) => update(content)}
          init={{
            height: 420,
            menubar: "file edit view insert format tools table",
            license_key: "gpl",
            branding: false,
            promotion: false,
            skin: false,
            content_css: false,
            plugins: [
              "advlist",
              "autolink",
              "charmap",
              "code",
              "codesample",
              "directionality",
              "emoticons",
              "fullscreen",
              "image",
              "insertdatetime",
              "link",
              "lists",
              "media",
              "nonbreaking",
              "pagebreak",
              "preview",
              "quickbars",
              "searchreplace",
              "table",
              "visualblocks",
              "visualchars",
              "wordcount",
            ],
            toolbar:
              "undo redo | blocks fontfamily fontsize | bold italic underline strikethrough | forecolor backcolor | " +
              "alignleft aligncenter alignright alignjustify | bullist numlist outdent indent | " +
              "link image mediaGallery media table | blockquote codesample charmap emoticons | ltr rtl | removeformat code preview fullscreen",
            quickbars_selection_toolbar: "bold italic | quicklink h2 h3 blockquote",
            quickbars_insert_toolbar: "quickimage quicktable",
            link_rel_list: [
              { title: "Follow", value: "" },
              { title: "No Follow", value: "nofollow" },
              { title: "No Index", value: "noindex" },
              { title: "No Follow + No Index", value: "nofollow noindex" },
            ],
            image_title: true,
            automatic_uploads: true,
            file_picker_types: "image",
            images_upload_handler: uploadImage,
            paste_data_images: true,
            setup: (editor) => {
              const setDefaultImageAlt = () => {
                editor.dom.select("img").forEach((image) => {
                  if (image.getAttribute("alt")) return;
                  const source = image.getAttribute("src") || "";
                  const filename = decodeURIComponent(source.split("/").pop()?.split("?")[0] || "image");
                  image.setAttribute("alt", toImageSlug(filename));
                });
              };

              editor.on("SetContent Change", setDefaultImageAlt);
              editorRef.current = editor;
              editor.ui.registry.addButton("mediaGallery", {
                text: "Gallery",
                tooltip: "Open media gallery",
                onAction: () => setMediaOpen(true),
              });
            },
            table_toolbar:
              "tableprops tabledelete | tableinsertrowbefore tableinsertrowafter tabledeleterow | tableinsertcolbefore tableinsertcolafter tabledeletecol",
            content_style:
              "body { font-family: Arial, Helvetica, sans-serif; font-size: 14px; } img { max-width: 100%; height: auto; }",
          }}
        />
      </div>
      <MediaLibraryModal
        open={mediaOpen}
        onClose={() => setMediaOpen(false)}
        initialFolder={folder_name}
        title={`${label || "Content"} Media Gallery`}
        onSelect={(image) => {
          const source = image?.image_full_path || image?.image_path || "";
          if (!source || !editorRef.current) return;
          const alt = toImageSlug(image?.name || source.split("/").pop() || "image");
          editorRef.current.insertContent(`<img src="${source}" alt="${alt}" />`);
          setMediaOpen(false);
        }}
      />
    </div>
  );
};

export default CKEditors;
